/**
 * Recaptcha rendering options
 * Allows client of the service to specify v2 or v3 of recaptcha, override siteKey, language, etc.
 */
export interface RecaptchaOptions {
    version?: RecaptchaVersion;
    language?: string;
    parentContainer?: HTMLElement;
    siteKey?: string;
    size?: RecaptchaWidgetSize;
    action?: string;
}

/**
 * Static class containing constants for widget size options (used by grecaptcha for rendering)
 */
export enum RecaptchaWidgetSize {
    COMPACT = 'compact',
    NORMAL = 'normal',
    INVISIBLE = 'invisible'
}

/**
 * Static class containing constants for RecaptchaWidgetStatus options
 * Statuses utilized for multi-use of a single recaptcha service, mostly required for V2 support,
 * because it relies on DIV tag challenge response and does not return an immediate response from the javascript service
 */
export enum RecaptchaWidgetStatus {
    NONE = 'NONE',
    INWORK = 'IN-WORK',
    LOADED = 'LOADED',
    ERROR = 'ERROR'
}

/**
 * Static class for containing constants for recaptcha version options
 * V2 is a computational challenge based implementation - typical of what you see used in the industry - which gets a token
 *   after potentiallyl challenging the user to solve a puzzle if flagged as suspicious
 * V3 is the new framework that issues no computational challenge but issues a score that our service can then use to determine whether
 *   or not to allow that traffic
 */
export enum RecaptchaVersion {
    V2 = 'v2',
    V3 = 'v3'
}

/**
 * Recaptcha service used to create shared recaptcha integration features for pages utilizing feature-components
 * Follows the singleton pattern and is set to com.TI.RecaptchaService on page load
 *
 * Exposes methods to integrate with recaptcha, such as appending recaptcha api.js script to the page, rendering the widget,
 *   and returning a token to be used for API calls and user actions.
 *
 * Steps for implementing recaptcha
 * 1) Get instance of RecaptchaService singleton
 *   const recaptchaService = com.TI.RecaptchaService.getInstance();
 * 2) Render recaptcha widget - optionally providing recaptchaOptions for rendering
 *   2a) For V2 invisible (most commonly used implementation) - default integration
 *     recaptchaService.getRecaptchaWidget({siteKey: 'your-site-key' }).then(widget => {//Recaptcha ready to fetch token})
 *   2b) For V3
 *     recaptchaService.getRecaptchaWidget({siteKey: 'your-v3-site-key', version: com.TI.RecaptchaService.RecaptchaVersion.V3}).then(widget => {//Recaptcha ready to fetch token})
 * 3) Call to get recaptcha token - recaptchaOptions required for V3
 *   3a) For V2
 *     recaptchaService.getRecaptchaResponse().then(token => {//Utilize your token})
 *   3b) For V3
 *     recaptchaService.getRecaptchaResponse({siteKey: 'your-v3-site-key', version: com.TI.RecaptchaService.RecaptchaVersion.V3}).then(token => {//Utilize your token})
 * 4) Add the token to the custom HTTP Header 'gRecaptcha-Response' when sending through the recaptcha proxy for validation of the token
 *
 * @author Taber Hust <a0225624@ti.com>
 */
export class RecaptchaService {

    //Expose static classes for easy options setting
    public static readonly RecaptchaWidgetStatus = RecaptchaWidgetStatus;
    public static readonly RecaptchaVersion = RecaptchaVersion;
    public static readonly RecaptchaWidgetSize = RecaptchaWidgetSize;

    private static _instance: RecaptchaService;

    private _instanceId;
    private _log: string[] = [];

    private _recaptchaService;
    private _recaptchaWidget;
    private _recaptchaWidgetId;
    private _recaptchaWidgetStatus = RecaptchaWidgetStatus.NONE;

    private _recaptchaResponseInput;

    private static readonly _defaultSitekey = '6LfuZZgaAAAAAGlkf7ZlvbBtieVFpRvJiLqocTFM';
    private static readonly _defaultSize = RecaptchaWidgetSize.INVISIBLE;
    private static readonly _callback = 'recaptchaCallback';
    private static readonly _expiredCallback = 'recaptchaExpiredCallback';
    private static readonly _errorCallback = 'recaptchaErrorCallback';
    private static readonly _loadCallback = 'recaptchaLoadCallback';

    private static readonly _recaptchaScriptSrc = `https://www.recaptcha.net/recaptcha/api.js?onload=${RecaptchaService._loadCallback}`;
    private _recaptchaScript;

    private constructor() {
        this._initializeCallbacks();

        this._instanceId = Math.random().toString(36).slice(-6);
    }

    public static getInstance(): RecaptchaService {
        if (!this._instance) {
            this._instance = new RecaptchaService();
        }

        return this._instance;
    }

    /**
     * Append recaptcha scripts to DOM and get recaptcha widget for initiating recaptcha token generation
     * Pre-requisite for getting recaptha tokens via getRecaptchaResponse
     *
     * In V2, this will actually render a DIV element on the DOM that will present the recaptcha computational challenge
     * In V3, this will only append the script (which will load some additional resources) but not DIV container is required
     *
     * @param recaptchaOptions - customizable recaptcha options for providing specific site keys, specifying versions, parent container for DIV tag, etc
     * @returns Promise of recaptcha widget (NOTE: This will be an empty HTMLElement for V3, but for V2 should contain the rendered recaptcha DIV element)
     */
    public getRecaptchaWidget(recaptchaOptions?: RecaptchaOptions): Promise<any> {
        return new Promise((resolve, reject) => {
            if (this._recaptchaWidgetStatus === RecaptchaWidgetStatus.NONE) {
                this.log(`getRecaptchaWidget: Creating recaptcha widget`);
                this._recaptchaWidgetStatus = RecaptchaWidgetStatus.INWORK;

                this._initializeRecaptchaElements(recaptchaOptions?.parentContainer);

                this._recaptchaScript = document.createElement('script');
                this._recaptchaScript.setAttribute('charset', 'utf-8');
                this._recaptchaScript.setAttribute('defer', 'true');
                this._recaptchaScript.setAttribute('async', 'true');

                //Create load callback for recaptcha script
                window[RecaptchaService._loadCallback] = () => {
                    this._recaptchaWidgetStatus = RecaptchaWidgetStatus.LOADED;
                    this.log(`getRecaptchaWidget: Recaptcha service loaded`);

                    if (window['grecaptcha']) {
                        this.log(`getRecaptchaWidget: Rendering recaptcha widget`);
                        this._recaptchaService = window['grecaptcha'];
                        if (recaptchaOptions?.version === RecaptchaVersion.V3) {
                            //No widget to actually render for V3. Resolve with empty widget
                        } else {
                            this._recaptchaWidgetId = this._recaptchaService.render(this._recaptchaWidget, {
                                'sitekey': recaptchaOptions?.siteKey ? recaptchaOptions.siteKey : RecaptchaService._defaultSitekey,
                                'size': recaptchaOptions?.size ? recaptchaOptions.size.toString() : RecaptchaService._defaultSize,
                                'callback': RecaptchaService._callback,
                                'expired-callback': RecaptchaService._expiredCallback,
                                'error-callback': RecaptchaService._errorCallback
                            });

                        }

                        resolve(this._recaptchaWidget);
                    } else {
                        this.log('getRecaptchaWidget: Script loaded without window[grecaptcha] being available');
                        reject('grecaptcha service not available');
                    }
                };

                this._recaptchaScript.onerror = (err) => {
                    this.log(`getRecaptchaWidget: Recaptcha script failed to load`, err);
                    this._recaptchaWidgetStatus = RecaptchaWidgetStatus.ERROR;
                    reject(err);
                };

                if (recaptchaOptions?.version === RecaptchaVersion.V3) {
                    this._recaptchaScript.setAttribute('src', `${RecaptchaService._recaptchaScriptSrc}&render=${recaptchaOptions?.siteKey ? recaptchaOptions.siteKey : RecaptchaService._defaultSitekey}`);
                } else {
                    if (recaptchaOptions?.language) {
                        this._recaptchaScript.setAttribute('src', `${RecaptchaService._recaptchaScriptSrc}&render=explicit&hl=${recaptchaOptions.language}`);
                    } else {
                        this._recaptchaScript.setAttribute('src', `${RecaptchaService._recaptchaScriptSrc}&render=explicit`);
                    }
                }

                document.head.appendChild(this._recaptchaScript);
            } else if (this._recaptchaWidget === RecaptchaWidgetStatus.INWORK) {
                this._recaptchaScript.onload = () => {
                    if (window['grecaptcha']) {
                        resolve(this._recaptchaWidget);
                    } else {
                        this.log('getRecaptchaWidget: Script loaded without window[grecaptcha] being available');
                        resolve(this._recaptchaWidget);
                    }
                };

                this._recaptchaScript.onerror = (err) => {
                    reject(err);
                };
            } else {
                this.log(`getRecaptchaWidget: Recaptcha widget called with widget status of ${this._recaptchaWidgetStatus}`);
                resolve(this._recaptchaWidget);
            }
        });
    }

    /**
     * Get recaptcha token to be sent in the HTTP call to the protected resource
     *
     * In V2, this will potentially initiate a computational challenge to the user, and only after completing the challenge will a token be returned
     *   Failing the computation challenge will reject the promise, detailing the error for reporting to the user
     * In V3, this will automatically get a token to be used without a computational challenge
     *   Though no challenge is issues, a score is assigned to that token that will then be evaluated by the recaptcha proxy
     *
     * @param recaptchaOptions optional recaptchaOptions for the response call. Necessary for V3 implementation
     * @returns Promise of string containing the token value to be provided in gRecaptcha-Response HTTP header
     */
    public getRecaptchaResponse(recaptchaOptions?: RecaptchaOptions): Promise<string> {
        return new Promise((resolve, reject) => {
            if (!this._recaptchaService) {
                this.log(`getRecaptchaResponse: Recaptcha service has not been instantiated`);
                reject({ error: 'Recaptcha service has not been instantiated' });
            } else {
                this._recaptchaResponseInput.value = '';
                //If version 3 - execute and use Promise to resolve the token value from the method call
                if (recaptchaOptions?.version === RecaptchaVersion.V3) {
                    //Make sure recaptcha service is ready then execute to get token
                    this._recaptchaService.ready(() => this._recaptchaService.execute(recaptchaOptions?.siteKey ? recaptchaOptions.siteKey : RecaptchaService._defaultSitekey, {
                        action: recaptchaOptions?.action ? recaptchaOptions.action : 'submit'
                    }).then(token => {
                        this._recaptchaResponseInput.value = token;
                        resolve(token);
                    }));
                } else {
                    //If not version 3 - render the widget and wait for the input to be set after solving the challenge
                    this._recaptchaService.execute();
                    const recaptchaResponseCheck = setInterval(() => {
                        if (this._recaptchaResponseInput.value) {
                            if (this._recaptchaResponseInput.value === 'EXPIRED' || this._recaptchaResponseInput.value === 'ERROR') {
                                reject({ error: `Recaptcha response is ${this._recaptchaResponseInput}` });
                            } else {
                                resolve(this._recaptchaResponseInput.value);
                            }

                            this._recaptchaResponseInput.value = '';
                            clearInterval(recaptchaResponseCheck);
                        }
                    }, 500);
                }
            }
        });
    }

    private _initializeCallbacks() {
        window[RecaptchaService._callback] = (token) => {
            this.log(`${RecaptchaService._callback}: Recaptcha response successfully retrieved`);
            this._recaptchaResponseInput.value = token;
            this._recaptchaService.reset(this._recaptchaWidgetId);
        };

        window[RecaptchaService._expiredCallback] = () => {
            this.log(`${RecaptchaService._expiredCallback}: Recaptcha expired`);
            this._recaptchaResponseInput.value = 'EXPIRED';
            this._recaptchaService.reset(this._recaptchaWidgetId);
        };

        window[RecaptchaService._errorCallback] = (err) => {
            this.log(`${RecaptchaService._errorCallback}: Recaptcha errored`, err);
            this._recaptchaResponseInput.value = 'ERROR';
            this._recaptchaService.reset(this._recaptchaWidgetId);
        };
    }

    private _initializeRecaptchaElements(parentContainer?: HTMLElement) {
        this._recaptchaResponseInput = document.createElement('input');
        this._recaptchaResponseInput.id = 'grecaptcha-response';
        this._recaptchaResponseInput.type = 'hidden';
        document.body.appendChild(this._recaptchaResponseInput);

        this._recaptchaWidget = document.createElement('div');
        this._recaptchaWidget.id = 'grecaptcha-widget';
        document.body.appendChild(this._recaptchaWidget);

        if (parentContainer) {
            parentContainer.appendChild(this._recaptchaResponseInput);
            parentContainer.appendChild(this._recaptchaWidget);
        } else {
            document.body.appendChild(this._recaptchaResponseInput);
            document.body.appendChild(this._recaptchaWidget);
        }
    }

    public getInstanceId(): string {
        return this._instanceId;
    }

    public static getLog(): string {
        return this.getInstance().getLog();
    }

    public static getLogArray(): string[] {
        return this.getInstance().getLogArray();
    }

    public getLog(): string {
        return `RecaptchaService [${this._instanceId}] Logs\n`.concat(this._log.join('\n'));
    }

    public getLogArray(): string[] {
        return [`RecaptchaService [${this._instanceId}] Logs`].concat(this._log);
    }

    private log(log: string, obj?: any) {
        this._log.push(log + (obj ? ': ' + JSON.stringify(obj) : ''));
    }
}
